/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/RoleCreationModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { refreshToken, signOut } from "../../utils/common.js";
import { createPortal } from "react-dom";
import styles from "./RoleCreationModal.module.css";

const RoleCreationModal = ({ open, onClose, onCreateRole }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tipoDeUsuario, setTipoDeUsuario] = useState("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [messageError, setMessageError] = useState("");
  const dialogRef = useRef(null);

  const getToken = () => {
    const token = localStorage.getItem("token");

    if (!!token && token !== "undefined") {
      return token;
    } else {
      logger.error(
        "Error trying to get the token to retrieve the users data. Token value:",
        { data: token },
      );
    }
  };

  const handleCreateRole = async () => {
    const roleData = {
      nombre: tipoDeUsuario,
    };

    const authToken = getToken();

    if (!authToken) {
      logger.error("Failed to get the token");
      return;
    }

    try {
      const options = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(roleData),
      };
      const response = await fetch(
        `${process.env.REACT_APP_ROOT_API}permission`,
        options,
      );

      if (response.ok) {
        setMessageError("");
        const responseData = await response.json();
        const newRole = responseData.userRole;
        onCreateRole(newRole);
        onClose();
        setShowSuccessMessage(true);
        setTimeout(() => {
          setShowSuccessMessage(false);
        }, 3000);
      } else {
        const errorData = await response.json();
        if (errorData?.message === "The incoming token has expired") {
          const newTokens = await refreshToken();
          logger.info("newTokens: ", { data: newTokens });
          if (!newTokens) {
            await signOut();
            navigate("/");
            return;
          }
          await handleCreateRole();
          return;
        }
        if (response.status === 409) {
          setMessageError(t("components.roleCreationModal.roleExistsError"));
        }
        logger.error("Failed to create user role:", {
          data: [response.status, errorData.message],
        });
      }
    } catch (error) {
      logger.error("Error:", { err: sanitizeError(error) });
    }
  };

  useEffect(() => {
    const handleClose = () => {
      setMessageError("");
      onClose();
    };

    const dialog = dialogRef.current;
    if (dialog) {
      dialog.addEventListener("close", handleClose);
    }

    if (open) {
      setTipoDeUsuario("");
      dialog.showModal();
    } else {
      dialog.close();
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener("close", handleClose);
      }
    };
  }, [open, onClose]);

  return (
    <>
      {open &&
        createPortal(<div className={styles.overlay}></div>, document.body)}
      <dialog ref={dialogRef} className={styles.modal}>
        <div className={styles.closeButtonContainer}>
          <button className={styles.closeButton} onClick={onClose}>
            &times;
          </button>
        </div>

        <div className={styles.modalContent}>
          <label className={styles.modalTitle}>
            {t("components.roleCreationModal.title")}
          </label>
          <div style={{}}>
            <label className={styles.modalLabel}>
              {t("components.roleCreationModal.roleLabel")}
            </label>
            <div className={styles.inputContainer}>
              <div className={styles.inputWrapper}>
                <input
                  type="text"
                  placeholder={t("components.roleCreationModal.rolePlaceholder")}
                  value={tipoDeUsuario}
                  onChange={(e) => setTipoDeUsuario(e.target.value)}
                  className={styles.modalInput}
                />
                {tipoDeUsuario && (
                  <button
                    className={styles.inputClearButton}
                    onClick={() => {
                      setMessageError("");
                      setTipoDeUsuario("");
                    }}
                  >
                    &times;
                  </button>
                )}
              </div>
            </div>
            {messageError && (
              <span className={styles.errorMessage}>{messageError}</span>
            )}
          </div>
        </div>

        <div className={styles.modalButtons}>
          <button onClick={onClose} className={styles.cancelButton}>
            {t("common.cancel")}
          </button>
          <button
            className={styles.createButton}
            onClick={handleCreateRole}
            disabled={!tipoDeUsuario}
          >
            {t("components.roleCreationModal.createButton")}
          </button>
        </div>
      </dialog>
      {showSuccessMessage && (
        <div className={styles.successMessage}>
          {t("components.roleCreationModal.successMessage")}
        </div>
      )}
    </>
  );
};

export default RoleCreationModal;
